import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, fmtDate } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Users, UserPlus, ShieldCheck, Database, Brain, Lock, Copy, Check, EnvelopeSimple, Crown,
  Desktop, ArrowCounterClockwise, SignOut, Key, LockKeyOpen, LockKey,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_STYLE = {
  admin: { c: "#ef4444", label: "Administrator" },
  pentester: { c: "#3b82f6", label: "Pentester" },
  client: { c: "#22c55e", label: "Client" },
};

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "" });
  const [copied, setCopied] = useState("");
  const [tempPw, setTempPw] = useState(null); // {email, temp}
  const [pwDialog, setPwDialog] = useState(false);
  const [myPw, setMyPw] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const isOwner = !!user.is_owner;

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
    if (isOwner) api.get("/invites").then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setTempPw({ email: data.email, temp: data.temp_password });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const unlockUser = async (u) => {
    try {
      await api.post(`/users/${u.id}/unlock`);
      toast.success(`Account unlocked for ${u.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const changeMyPassword = async () => {
    if (myPw.new_password !== myPw.confirm_password) { toast.error("Passwords do not match"); return; }
    try {
      await api.post("/auth/change-my-password", myPw);
      toast.success("Password updated");
      setPwDialog(false);
      setMyPw({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copyTemp = async () => {
    await navigator.clipboard.writeText(tempPw.temp);
    toast.success("Temporary password copied");
  };

  const resetTwoFA = async (u) => {
    try {
      await api.post(`/users/${u.id}/reset-2fa`);
      toast.success(`2FA reset for ${u.email}`, { description: "They'll re-enroll on next sign-in." });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const revokeSession = async (id) => {
    try {
      await api.delete(`/sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      toast.success("Session signed out");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const inviteLink = (token) => `${window.location.origin}/invite/${token}`;

  const createInvite = async () => {
    try {
      const { data } = await api.post("/invites", form);
      toast.success("Invite created", { description: "Copy the link and send it to your invitee." });
      setOpen(false);
      setForm({ email: "", name: "" });
      setInvites((prev) => [data, ...prev]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/invites/${id}`);
      setInvites((prev) => prev.filter((i) => i.id !== id));
      toast.success("Invite revoked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copy = async (token) => {
    await navigator.clipboard.writeText(inviteLink(token));
    setCopied(token);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(""), 1500);
  };

  const inviteStatus = (i) => (i.used ? "Used" : i.expires_at < new Date().toISOString() ? "Expired" : "Pending");

  return (
    <div className="fade-up">
      <PageHeader overline="Administration" title="Settings"
        subtitle="Access control, invites, and platform residency configuration." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <InfoCard icon={ShieldCheck} title="Authentication" value="Password + TOTP 2FA" note="Owner-only + invited pentesters" />
        <InfoCard icon={Database} title="Data residency" value="Australia East" note="Cosmos DB for MongoDB" />
        <InfoCard icon={Brain} title="AI narratives" value="Azure OpenAI" note="In-region, no data egress" />
      </div>

      {/* Invites — owner only */}
      {isOwner && (
        <Card className="bg-[#0f172a] border-[#1e293b] rounded-lg overflow-hidden mb-6">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#1e293b]">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={18} className="text-blue-400" weight="bold" />
              <h2 className="font-head text-lg font-semibold text-white">Invitations</h2>
              <span className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-400 border border-amber-500/40 rounded px-1.5 py-0.5">
                <Crown size={11} weight="fill" /> OWNER ONLY
              </span>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="invite-user-button" size="sm" className="bg-blue-600 hover:bg-blue-500 text-white">
                  <UserPlus size={16} weight="bold" className="mr-1" /> Send Invite
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0b1220] border-[#1e293b] text-white">
                <DialogHeader><DialogTitle>Invite a Pentester</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-300">Invitee email</Label>
                    <Input data-testid="invite-email-input" type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1 bg-[#0f172a] border-[#1e293b]" placeholder="analyst@company.com" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Name (optional)</Label>
                    <Input data-testid="invite-name-field" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 bg-[#0f172a] border-[#1e293b]" />
                  </div>
                  <div className="rounded-md bg-blue-500/10 border border-blue-500/30 px-3 py-2.5 text-sm text-blue-200">
                    They'll create their own account (Pentester) via the link and set up 2FA. They cannot invite others.
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="invite-create-submit" onClick={createInvite} disabled={!form.email}
                    className="bg-blue-600 hover:bg-blue-500 text-white">Create Invite Link</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {invites.length === 0 ? (
            <div className="px-5 py-8 text-center text-slate-500 text-sm">No invites yet. Send one to add a pentester.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#1e293b] hover:bg-transparent">
                  <TableHead className="overline text-slate-500">Email</TableHead>
                  <TableHead className="overline text-slate-500">Status</TableHead>
                  <TableHead className="overline text-slate-500">Created</TableHead>
                  <TableHead className="overline text-slate-500 text-right">Link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((i) => {
                  const st = inviteStatus(i);
                  return (
                    <TableRow key={i.id} data-testid={`invite-row-${i.id}`} className="border-[#1e293b] hover:bg-slate-800/40">
                      <TableCell className="text-white font-mono text-sm">{i.email}</TableCell>
                      <TableCell>
                        <span className={`text-xs font-medium ${st === "Pending" ? "text-blue-400" : st === "Used" ? "text-emerald-400" : "text-slate-500"}`}>{st}</span>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">{fmtDate(i.created_at)}</TableCell>
                      <TableCell className="text-right">
                        {st === "Pending" ? (
                          <div className="inline-flex items-center gap-1">
                            <Button data-testid={`copy-invite-${i.id}`} size="sm" variant="ghost" onClick={() => copy(i.token)}
                              className="text-slate-300 hover:text-white hover:bg-slate-800 h-8">
                              {copied === i.token ? <Check size={15} className="text-emerald-400" /> : <Copy size={15} />}
                              <span className="ml-1">Copy link</span>
                            </Button>
                            <Button data-testid={`revoke-invite-${i.id}`} size="sm" variant="ghost" onClick={() => revoke(i.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8">Revoke</Button>
                          </div>
                        ) : <span className="text-slate-600 text-xs">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* Active Sessions */}
      <Card className="bg-[#0f172a] border-[#1e293b] rounded-lg overflow-hidden mb-6">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-[#1e293b]">
          <Desktop size={18} className="text-blue-400" weight="bold" />
          <h2 className="font-head text-lg font-semibold text-white">Active Sessions</h2>
          <span className="text-xs text-slate-500">{isOwner ? "all users" : "your sessions"}</span>
        </div>
        {sessions.length === 0 ? (
          <div className="px-5 py-8 text-center text-slate-500 text-sm">No active sessions.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#1e293b] hover:bg-transparent">
                {isOwner && <TableHead className="overline text-slate-500">User</TableHead>}
                <TableHead className="overline text-slate-500">IP</TableHead>
                <TableHead className="overline text-slate-500">Signed in</TableHead>
                <TableHead className="overline text-slate-500">Last active</TableHead>
                <TableHead className="overline text-slate-500 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((s) => (
                <TableRow key={s.id} data-testid={`session-row-${s.id}`} className="border-[#1e293b] hover:bg-slate-800/40">
                  {isOwner && (
                    <TableCell className="text-white">
                      <span className="inline-flex items-center gap-1.5">
                        {s.is_owner && <Crown size={13} weight="fill" className="text-amber-400" />}
                        {s.user_name}
                        <span className="text-slate-500 font-mono text-xs">· {s.user_email}</span>
                      </span>
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-slate-300 text-sm">{s.ip || "—"}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{fmtDate(s.created_at)}</TableCell>
                  <TableCell className="text-slate-400 text-sm">
                    {fmtDate(s.last_seen)}
                    {s.is_current && <span className="ml-2 text-emerald-400 text-xs">(this device)</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`revoke-session-${s.id}`} size="sm" variant="ghost" onClick={() => revokeSession(s.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8">
                      <SignOut size={15} className="mr-1" /> {s.is_current ? "Sign out" : "Force out"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Users */}
      <Card className="bg-[#0f172a] border-[#1e293b] rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1e293b]">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-blue-400" weight="bold" />
            <h2 className="font-head text-lg font-semibold text-white">Users &amp; Roles</h2>
          </div>
          <Dialog open={pwDialog} onOpenChange={setPwDialog}>
            <DialogTrigger asChild>
              <Button data-testid="change-my-password-button" size="sm" variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-slate-800">
                <Key size={15} className="mr-1.5" /> Change my password
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0b1220] border-[#1e293b] text-white">
              <DialogHeader><DialogTitle>Change my password</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300">Current password</Label>
                  <Input data-testid="my-current-password" type="password" value={myPw.current_password}
                    onChange={(e) => setMyPw({ ...myPw, current_password: e.target.value })}
                    className="mt-1 bg-[#0f172a] border-[#1e293b]" />
                </div>
                <div>
                  <Label className="text-slate-300">New password</Label>
                  <Input data-testid="my-new-password" type="password" value={myPw.new_password}
                    onChange={(e) => setMyPw({ ...myPw, new_password: e.target.value })}
                    className="mt-1 bg-[#0f172a] border-[#1e293b]" placeholder="At least 8 characters" />
                </div>
                <div>
                  <Label className="text-slate-300">Confirm new password</Label>
                  <Input data-testid="my-confirm-password" type="password" value={myPw.confirm_password}
                    onChange={(e) => setMyPw({ ...myPw, confirm_password: e.target.value })}
                    className="mt-1 bg-[#0f172a] border-[#1e293b]" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="my-password-submit" onClick={changeMyPassword}
                  disabled={!myPw.current_password || myPw.new_password.length < 8}
                  className="bg-blue-600 hover:bg-blue-500 text-white">Update password</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-[#1e293b] hover:bg-transparent">
              <TableHead className="overline text-slate-500">Name</TableHead>
              <TableHead className="overline text-slate-500">Email</TableHead>
              <TableHead className="overline text-slate-500">Role</TableHead>
              <TableHead className="overline text-slate-500">2FA</TableHead>
              <TableHead className="overline text-slate-500">Created</TableHead>
              {isOwner && <TableHead className="overline text-slate-500 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const rs = ROLE_STYLE[u.role] || ROLE_STYLE.pentester;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="border-[#1e293b] hover:bg-slate-800/40">
                  <TableCell className="text-white font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      {u.is_owner && <Crown size={14} weight="fill" className="text-amber-400" />}
                      {u.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-300 font-mono text-sm">{u.email}</TableCell>
                  <TableCell>
                    <span className="rounded px-2 py-0.5 text-xs font-medium" style={{ color: rs.c, backgroundColor: `${rs.c}18`, border: `1px solid ${rs.c}40` }}>
                      {u.is_owner ? "Owner" : rs.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    {u.account_locked && (
                      <span className="inline-flex items-center gap-1 text-red-400 text-xs mr-2"><LockKey size={13} weight="fill" /> Locked</span>
                    )}
                    {u.totp_enabled
                      ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><ShieldCheck size={14} weight="fill" /> Enabled</span>
                      : <span className="text-slate-500 text-xs">Pending</span>}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">{fmtDate(u.created_at)}</TableCell>
                  {isOwner && (
                    <TableCell className="text-right whitespace-nowrap">
                      {u.account_locked && (
                        <Button data-testid={`unlock-${u.id}`} size="sm" variant="ghost" onClick={() => unlockUser(u)}
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-8">
                          <LockKeyOpen size={15} className="mr-1" /> Unlock
                        </Button>
                      )}
                      <Button data-testid={`reset-password-${u.id}`} size="sm" variant="ghost" onClick={() => resetPassword(u)}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 h-8">
                        <Key size={15} className="mr-1" /> Reset password
                      </Button>
                      <Button data-testid={`reset-2fa-${u.id}`} size="sm" variant="ghost"
                        onClick={() => resetTwoFA(u)} disabled={!u.totp_enabled}
                        className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-8 disabled:opacity-30">
                        <ArrowCounterClockwise size={15} className="mr-1" /> Reset 2FA
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Temporary password reveal (after admin reset) */}
      <Dialog open={!!tempPw} onOpenChange={(v) => !v && setTempPw(null)}>
        <DialogContent className="bg-[#0b1220] border-[#1e293b] text-white" data-testid="temp-password-dialog">
          <DialogHeader><DialogTitle>Temporary password issued</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">
            Share this one-time temporary password with <span className="text-blue-300 font-mono">{tempPw?.email}</span> offline.
            They'll be required to set a new password on next sign-in.
          </p>
          <div className="flex items-center gap-2 rounded-md bg-[#0f172a] border border-[#1e293b] px-3 py-2.5 mt-2">
            <code data-testid="temp-password-value" className="flex-1 text-blue-300 font-mono break-all">{tempPw?.temp}</code>
            <Button data-testid="copy-temp-password" size="sm" variant="ghost" onClick={copyTemp}
              className="text-slate-300 hover:text-white hover:bg-slate-800"><Copy size={15} /></Button>
          </div>
          <DialogFooter>
            <Button data-testid="temp-password-done" onClick={() => setTempPw(null)}
              className="bg-blue-600 hover:bg-blue-500 text-white">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoCard({ icon: Icon, title, value, note }) {
  return (
    <Card className="bg-[#0f172a] border-[#1e293b] rounded-lg p-4">
      <div className="flex items-center gap-2 overline text-slate-500 mb-2"><Icon size={15} weight="bold" className="text-blue-500" /> {title}</div>
      <div className="font-head text-lg font-semibold text-white">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{note}</div>
    </Card>
  );
}
